import { IconButton, ListItem, makeStyles } from '@material-ui/core';
import { DragHandle } from '@material-ui/icons';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import classNames from 'classnames';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { COLORS } from '../../theme/variables';
import { createMarkup } from '../../utils/functions';
import { ConfirmButton, Text } from '../common';
import { Values as FormValues } from '../forms/consent';

interface ConsentProps {
  consent: {
    id: string;
    type: 'Founder' | 'Mentor';
    body: string;
  };
  updating?: boolean;
  deleting?: boolean;
  onEdit: () => void;
  onRemove: (values: FormValues, callback: () => any) => any;
  readOnly?: boolean;
  index?: number;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  dragging?: boolean;
  dragLoading?: boolean;
  showType?: boolean;
}

const useStyles = makeStyles((theme) => ({
  listItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    padding: 0,
    '@media (max-width: 700px)': {
      flexDirection: 'column-reverse',
      alignItems: 'flex-end',
      gap: 8,
    },
  },
  draggingListItemContainer: {
    background: 'rgb(235,235,235)',
  },
  listItemContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    border: `solid 1px ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    borderRadius: 4,
    padding: 16,
    paddingTop: 40,
    boxSizing: 'border-box',
  },
  dragHandleContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.2s ease-out',
  },
  preventDrag: {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  disabledContainer: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  itemActions: {
    display: 'flex',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  body: {
    wordBreak: 'break-all',
  },
  attachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '20px 0 -16px -16px',
  },
  attachment: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 0 16px 16px',

    [theme.breakpoints.up(800)]: {
      width: '50%',
    },
  },
  bodyText: {
    wordBreak: 'break-word',

    '& ul, ol, li, b, i, u, h1, h2, pre': {
      margin: 'revert',
      padding: 'revert',
      fontWeight: 'revert',
      fontStyle: 'revert',
      listStyle: 'revert',
      border: 'revert',
      fontSize: 'revert',
      font: 'revert',
      verticalAlign: 'revert',
      lineHeight: 'normal !important',
    },
  },
}));

function Consent({
  consent,
  updating,
  deleting,
  onEdit,
  onRemove,
  readOnly = false,
  index,
  dragHandleProps,
  dragging,
  dragLoading,
  showType = true,
}: ConsentProps) {
  const classes = useStyles();
  const { rb } = useResourceBundles();

  const handleConsentRemove = (callback: () => any) => {
    onRemove(consent, callback);
  };

  return (
    <ListItem className={classes.listItem} data-testid={`consent-${index}`}>
      <div
        className={classNames(
          dragging ? classes.draggingListItemContainer : '',
          classes.listItemContainer,
          {
            [classes.disabledContainer]: dragLoading,
          },
        )}>
        {dragHandleProps && (
          <div
            className={classNames(classes.dragHandleContainer, {
              [classes.preventDrag]: dragLoading,
            })}
            {...dragHandleProps}>
            <DragHandle />
          </div>
        )}
        {showType && (
          <div className={classes.title}>
            <Text color={COLORS.COLOR_GRAY_DARKENED} variant='normal2'>
              Type: {consent.type === 'Mentor' ? rb('mentor-u') : consent.type}
            </Text>
          </div>
        )}
        <div>
          <Text className={classes.body} variant='normal'>
            <div
              className={classes.bodyText}
              dangerouslySetInnerHTML={createMarkup(consent.body)}
            />
          </Text>
        </div>
      </div>
      {!readOnly && (
        <div className={classes.itemActions}>
          <IconButton
            onClick={onEdit}
            data-testid={`form-consent-edit-${index}`}>
            <EditIcon />
          </IconButton>
          <ConfirmButton
            loading={deleting}
            title='Remove the commitment?'
            body="Sure you want to remove the commitment? Changes can't be undone"
            successProps={{
              btnLabel: 'Remove',
              onSuccess: handleConsentRemove,
            }}
            buttonRenderer={({ onClick }: { onClick: () => void }) => (
              <IconButton
                onClick={onClick}
                data-testid={`form-consent-delete-${index}`}>
                <DeleteIcon />
              </IconButton>
            )}
          />
        </div>
      )}
    </ListItem>
  );
}

export default Consent;
